import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { useContent } from "@/contexts/ContentContext";
import { Save, Check } from "lucide-react";

const FIELDS = [
  { key: "hero_title", label: "Hero — Title", type: "input" },
  { key: "hero_subtitle", label: "Hero — Subtitle", type: "textarea" },
  { key: "hero_cta_label", label: "Hero — CTA Label", type: "input" },
  { key: "marquee_text", label: "Marquee Text", type: "input" },
  { key: "feature_1_title", label: "Feature 1 — Title", type: "input" },
  { key: "feature_1_body", label: "Feature 1 — Body", type: "textarea" },
  { key: "feature_2_title", label: "Feature 2 — Title", type: "input" },
  { key: "feature_2_body", label: "Feature 2 — Body", type: "textarea" },
  { key: "feature_3_title", label: "Feature 3 — Title", type: "input" },
  { key: "feature_3_body", label: "Feature 3 — Body", type: "textarea" },
  { key: "product_intro", label: "Product Page — Intro", type: "textarea" },
  { key: "sizes_note", label: "Sizes — Subtitle", type: "textarea" },
  { key: "sizes_fit_note", label: "Sizes — Fit Guidance Box", type: "textarea" },
  { key: "contact_email", label: "Contact Email", type: "input" },
  { key: "contact_phone", label: "Contact Phone", type: "input" },
  { key: "contact_address", label: "Contact Address", type: "textarea" },
  { key: "footer_tagline", label: "Footer Tagline", type: "input" },
];

export default function AdminContent() {
  const { content, refresh } = useContent();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (content) setForm(content);
  }, [content]);

  const handleChange = (key, value) => {
    setForm({ ...form, [key]: value });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/content", form);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-content-page">
        <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-8 sticky top-0 bg-[#F4F4F5] z-10 py-2">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#1E5BB8] mb-2">
              Admin / Content
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">
              Edit Site Content
            </h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="content-save"
            className="inline-flex items-center gap-3 bg-[#1E5BB8] text-[#091526] font-bold uppercase text-sm tracking-[0.2em] px-6 py-4 border-2 border-[#091526] disabled:opacity-50"
          >
            {saved ? <><Check size={16} /> Saved</> : saving ? "Saving…" : <><Save size={16} /> Save Changes</>}
          </button>
        </div>

        {error && (
          <div className="border-2 border-red-600 bg-red-50 text-red-700 px-4 py-3 text-sm mb-6" data-testid="content-error">
            {error}
          </div>
        )}

        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key} className="border-2 border-[#091526] bg-white p-5">
              <label className="block font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2">
                {f.label}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  value={form[f.key] || ""}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  rows={3}
                  data-testid={`content-field-${f.key}`}
                  className="w-full bg-[#F4F4F5] border-2 border-[#091526] px-3 py-2 font-mono text-sm focus:outline-none focus:border-[#1E5BB8] resize-none"
                />
              ) : (
                <input
                  type="text"
                  value={form[f.key] || ""}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  data-testid={`content-field-${f.key}`}
                  className="w-full bg-[#F4F4F5] border-2 border-[#091526] px-3 py-2 font-mono text-sm focus:outline-none focus:border-[#1E5BB8]"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
